<template>
  <div class="federated-search-searchbar">
    <div
      :name="`${id}-form`"
      class="form__large"
      v-on:submit="handleFormSubmit"
      @keydown.tab="isTabPressed = true"
    >
      <form :action="action" ref="searchForm">
        <div
          :id="`${id}-status`"
          role="alert"
          aria-atomic="true"
          aria-live="polite"
          class="sr-only"
          v-html="status"
        ></div>
        <label :id="`${id}-label`" class="sr-only">{{ inputLabel }}</label>
        <span :id="`${id}-form-filter__inputHint`" style="display: none">{{
          filterLabel
        }}</span>
        <select
          :aria-describedby="`${id}-form-filter__inputHint`"
          v-if="displayFilters"
          autocomplete="off"
          :id="`${id}-select`"
          :class="`form__large__search-filter-select ${isFocused ? 'form__large__search-filter-select-focus-visible' : ''}`"
          v-model="filter"
          name="filters"
          @focus="isFocused = isTabPressed"
          @blur="isFocused = false"
          @mousedown="isTabPressed = false"
        >
          <optgroup>
            <option
              v-for="(filter, index) in this.filters"
              v-bind:key="index"
              :value="filter.value"
            >
              {{ filter.display }}
            </option>
          </optgroup>
        </select>
        <input
          :class="`form__input--large ${
            displayFilters ? 'form__large__input-with-select' : ''
          }`"
          type="text"
          name="s"
          autocomplete="off"
          :placeholder="isDesktop() ? placeholder : 'search'"
          :aria-labelledby="`${id}-label`"
          :aria-describedby="`${id}-form__inputHint`"
          ref="searchTextInput"
          v-model="lastSearchText"
        />
        <label :id="`${id}-label`" class="sr-only">{{ inputLabel }}</label>

        <span :id="`${id}-form__inputHint`" style="display: none">{{
          inputLabel
        }}</span>

        <button type="button" v-on:click="submitClick">
          <ahc-icon
            color="white"
            icon-name="search"
            width="48"
            height="35"
          ></ahc-icon>
          <span class="sr-only">Submit search</span>
        </button>
      </form>
    </div>
    <div >
      <span v-if="helpText" class="federated-search-searchbar__help-text">
        {{ helpText}}
        <button class="federated-search-searchbar__help-button" ref="tooltip"
          v-tooltip="{ content: guideContents, trigger: isMobile() ? 'click' : 'hover focus', position: 'auto', classes: 'federated-search-searchbar__help-tooltip'  }"
          type="button">
            <svg aria-hidden="true" role="img">
              <use
                :xlink:href="`/app/themes/ahc/src/assets/images/icons.svg#search-info`"
              />
            </svg>
        </button>
      </span>
    </div>
  </div>
</template>

<script lang="js">
import algoliasearch from 'algoliasearch/lite';
import ahcIcon from "../icon/icon.vue";

export default {
  name: 'ahc-federated-search-searchbar',
  components: {
    ahcIcon,
  },
  props: {
    submitHandler: {
      type: Function,
      required: false
    },
    action: {
      type: String,
      required: false,
      default: '/',
    },
    placeholder: {
      type: String,
      required: false,
      default: 'Search',
    },
    defaultValue: {
      type: String,
      required: false,
      default: '',
    },
    id: {
      type: String,
      required: false,
      default: 'federated-search-input'
    },
    inputLabel: {
      type: String,
      required: false,
      default: 'Input field for search'
    },
    filterLabel: {
      type: String,
      required: false,
      default: 'Prefilter select dropdown'
    },
    displayFilters: {
      type: Boolean,
      required: false,
      default: false
    },
    config: {
      type: Object,
      required: false
    },
    helpText: {
      type: String,
      required: false
    },
    guideContents: {
      type: String,
      required: false
    },
  },
  data() {
    return {
      filter: '',
      lastSearchText: '',
      isLoading: false,
      status: '',
      isFocused: false,
      isTabPressed: true,
      filters: [{display: 'Everything', value: ''}],
      innerWidth: window.innerWidth,
      index: this.config ? algoliasearch(
          this.config.application_id,
          this.config.application_key
        ).initIndex(this.config.index_name) : null
    }
  },
  watch: {
    status() {
      setTimeout(() => {
        this.status = '';
      }, 3000);
    }
  },
  methods: {
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },
    isDesktop () {
      return this.innerWidth > 580;
    },
    handleFormSubmit (event) {
      if (this.submitHandler) {
        event.preventDefault()
        this.submitHandler(this.lastSearchText);
      }
    },
    submitClick() {
      if (this.submitHandler) {
        this.submitHandler(this.lastSearchText);
      } else {
        this.$refs.searchForm.submit();
      }
    },
    windowResize: (context) => { context.innerWidth = window.innerWidth },
  },
  unmounted() {
    window.removeEventListener('resize', this.windowResize);
  },
  mounted() {
    if (this.defaultValue) this.lastSearchText = this.defaultValue;
    window.addEventListener('resize', () => {this.windowResize(this)});
    if (this.displayFilters) {
      this.index.search('', {facets: ["*"]})
        .then(({facets}) => {
          const format = Object.keys(facets['Format']).map((filter) => {return {display:filter, value: `Format:${filter}`}});
          const itemType = Object.keys(facets['Item Type']).map((filter) => {return {display:filter, value: `Item Type:${filter}`}});
          this.filters = [{display: 'Everything', value: ''}, ...format, ...itemType];
        });
    }
  },

}
</script>

<style lang="scss">
@import "federated-search-searchbar";
</style>
